import type { ReactNode } from 'react';
import { useState } from 'react';
import styled from 'styled-components';
import { getStylusClassName } from '@elfsight/widget-utils';
import { useRect } from 'react-use-rect';

interface GridLayoutLegacyProps {
  children: ReactNode[];
  appAlias: string;
  columnBasis?: number;
  gutter?: {
    v: number;
    h: number;
  };
  columns?: number;
}

export function GridLayoutLegacy({
  children,
  appAlias,
  columnBasis = 300,
  gutter = {
    v: 24,
    h: 24
  },
  columns
}: GridLayoutLegacyProps) {
  const { v: verticalGutter, h: horizontalGutter } = gutter;

  const [columnsCount, setColumnsCount] = useState(1);

  const getClassName = (component: string) =>
    getStylusClassName({ appAlias, component, fileName: 'grid' });

  const items = children.map((item, id) => {
    if (item) {
      return (
        <Item
          key={id}
          className={getClassName('item')}
          _autoColumns={columnsCount}
          _columns={columns}
          _verticalGutter={verticalGutter}
          _horizontalGutter={horizontalGutter}
        >
          {item}
        </Item>
      );
    }

    return null;
  });

  const [containerRef] = useRect(
    ({ width }) => {
      const nextColumnsCount = Math.floor(width / columnBasis) || 1;

      if (nextColumnsCount !== columnsCount && nextColumnsCount > 0) {
        setColumnsCount(nextColumnsCount);
      }
    },
    { resize: true }
  );

  return (
    <Component
      ref={containerRef}
      className={getClassName('component')}
      _verticalGutter={verticalGutter}
      _horizontalGutter={horizontalGutter}
    >
      {items}
    </Component>
  );
}

const Component = styled.div<{
  _verticalGutter: number;
  _horizontalGutter: number;
}>`
  display: flex;
  flex-wrap: wrap;
  overflow: hidden;
  margin: 0 -${({ _horizontalGutter }) => _horizontalGutter}px -${({
      _verticalGutter
    }) => _verticalGutter}px
    0;
`;

const Item = styled.div<{
  _verticalGutter: number;
  _horizontalGutter: number;
  _autoColumns: number;
  _columns?: number;
}>`
  display: block;
  box-sizing: border-box;
  width: ${({ _columns, _autoColumns }) =>
    _columns ? 100 / _columns : 100 / _autoColumns}%;
  padding: 0 ${({ _horizontalGutter }) => _horizontalGutter}px
    ${({ _verticalGutter }) => _verticalGutter}px 0;
`;
